import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Users,
  Target,
  Briefcase,
  Award,
  ChevronRight,
  Trophy,
  Heart,
} from "lucide-react";

const About = () => {
  const { t } = useTranslation();

  const awards = [
    { year: 2020, award: "MBE Advocate of the Year" },
    { year: 2019, award: "Supplier Diversity Excellence Award" },
    { year: 2018, award: "AMCA Chairman's Award" },
    { year: 2016, award: "AZSBDC/PTAC Success Award" },
    { year: 2015, award: "PSMSDC Supplier of the Year Award" },
    { year: 2015, award: "AMCA Chairman's Award" },
    { year: 2013, award: "AMCA Advocate of the Year" },
    { year: 2011, award: "GCMSDC MBE Advocate of the Year Award" },
    { year: 2009, award: "GCMSDC MBE Supplier of the Year – Class II" },
    {
      year: 2009,
      award:
        "Arizona State University/WP Carey Spirit of Enterprise Awards Finalist",
    },
    { year: 2009, award: "GCMSDC MBE Advocate of the Year Award" },
    { year: 2009, award: "ULBC Service Award" },
    {
      year: 2007,
      award:
        "Salt River Project Supplier Diversity Community Advocate All-Star Award",
    },
    {
      year: 2004,
      award:
        "National Minority Enterprise Development Week Individual Advocate Award",
    },
    {
      year: 2003,
      award:
        "Small Business Advocate of the year by the ULBC (United Latino Business Coalition)",
    },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-green-50">
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            {t('about.mainTitle', 'About Scott Business Group')}
          </h1>

          {/* Company Overview with Group Image */}
          <div className="bg-white shadow-lg rounded-lg p-8 mb-12 flex flex-col lg:flex-row items-center lg:items-start gap-8 border-l-4 border-orange-500">
            <div className="lg:w-2/3">
              <p className="text-lg text-gray-700 mb-6">
                {t('about.overview', 'Scott Business Group is a leading recruitment agency dedicated to connecting talented professionals with outstanding career opportunities. With years of experience and a vast network of industry connections, we strive to make the perfect match between job seekers and employers.')}
              </p>
              <p className="text-lg text-gray-700 mb-6">
                {t('about.missionStatement', 'Our mission is to empower businesses and individuals by facilitating meaningful career connections. We believe in the power of the right job fit to transform lives and drive business success.')}
              </p>
              <p className="text-lg text-gray-700">
                {t('about.expertise', 'At Scott Business Group, we combine industry expertise with personalized service to ensure that both employers and job seekers achieve their goals.')}
              </p>
            </div>
            <div className="lg:w-1/3 flex justify-center">
              <img
                src="/images/GROUP-1-300x165.jpg"
                alt={t('about.teamImageAlt', 'Scott Business Group Team')}
                className="rounded-lg shadow-md max-w-full h-auto"
              />
            </div>
          </div>

          {/* Founders Section */}
          <div className="bg-green-100/50 shadow-lg rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6 text-center">
              {t('about.founders.title', 'Our Founders')}
            </h2>
            <div className="flex flex-col md:flex-row items-center md:items-start justify-center gap-8">
              <img
                src="/images/SBG-1500x267-1500x267.png"
                alt={t('about.founders.imageAlt', 'Greg and Millie Scott')}
                className="w-64 h-64 object-cover rounded-full shadow-md"
              />
              <div className="max-w-xl">
                <p className="text-lg text-gray-700 mb-4">
                  {t('about.founders.description', 'Scott Business Group was founded by entrepreneurs Greg and Millie Scott. With over 50 years of talent acquisition experience between them, they have the industry expertise and the vision to create success.')}
                </p>
                <p className="text-lg text-gray-700">
                  {t('about.founders.impact', 'Their combined experience and passion for connecting the right talent with the right opportunities have been the driving force behind Scott Business Group\'s success and reputation in the industry.')}
                </p>
              </div>
            </div>
          </div>

          {/* Corporate Social Responsibility Section */}
          <div className="bg-green-100 shadow-lg rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6 text-center flex items-center justify-center">
              <Heart className="w-10 h-10 text-green-600 mr-3" />
              {t('about.csr.title', 'Corporate Social Responsibility')}
            </h2>
            <div className="text-lg text-gray-700 mb-6">
              <p className="mb-4">
                {t('about.csr.intro', 'Over two decades ago, Greg and Millie Scott embarked on an inspiring journey by founding Scott Business Group, fueled by a deep-seated commitment to excellence and giving back to the community. Their vision was clear: to embed Corporate Social Responsibility (CSR) into the very fabric of our company culture. Today, we proudly carry that torch, encouraging every team member to actively engage and serve our local communities.')}
              </p>
              <p className="mb-4">
                {t('about.csr.advocacy', 'At Scott Business Group, we believe in the power of small businesses and the invaluable contributions of minority-owned enterprises, women-owned ventures, and those led by service-disabled veterans. These groups are not just part of the community; they form its backbone, driving economic growth and innovation.')}
              </p>
              <p className="mb-6">
                {t('about.csr.leadership', 'Our leadership passionately leads by example, participating in various community initiatives that have earned us recognition from local organizations and the communities we strive to uplift.')}
              </p>
              <h3 className="text-xl font-semibold mb-4">
                {t('about.csr.initiatives.title', 'Our CSR initiatives are diverse and impactful, showcasing our unwavering commitment to social responsibility. Here are some highlights of our efforts:')}
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{t('about.csr.initiatives.veterans', 'Actively recruiting and providing career opportunities for veterans serving our nation.')}</li>
                <li>{t('about.csr.initiatives.cleanup', 'Engaging in community clean-ups and environmental projects that keep our neighborhoods beautiful and sustainable.')}</li>
                <li>{t('about.csr.initiatives.youth', 'Mentoring youth through programs that empower the next generation and foster personal development.')}</li>
                <li>{t('about.csr.initiatives.women', 'Championing education and employment opportunities for women, breaking down barriers, and paving the way for future leaders.')}</li>
                <li>{t('about.csr.initiatives.development', 'Contributing our expertise to professional development and advisory committees, creating a brighter future for our community.')}</li>
                <li>{t('about.csr.initiatives.diversity', 'Serving on boards that advocate for diversity and inclusion, ensuring every voice is heard and valued.')}</li>
                <li>{t('about.csr.initiatives.humanitarian', 'Rolling up our sleeves for humanitarian efforts and dedicating our time and resources to community service initiatives.')}</li>
                <li>{t('about.csr.initiatives.volunteering', 'Volunteering across various capacities to support those in need, showing that we can make a difference together.')}</li>
                <li>{t('about.csr.initiatives.homeless', 'Providing employment services geared towards helping individuals experiencing homelessness find stable footing.')}</li>
              </ul>
              <p className="mt-6">
                {t('about.csr.closing', 'At Scott Business Group, we are not just a business but a dedicated force for good. Together, we build stronger communities and brighter futures, one meaningful initiative at a time. Join us in our mission to make a lasting impact!')}
              </p>
            </div>
          </div>

          {/* Working In Our Community Section */}
          <div className="bg-white shadow-lg rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6 text-center flex items-center justify-center">
              <Trophy className="w-10 h-10 text-orange-500 mr-3" />
              {t('about.community.title', 'Working In Our Community')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {awards.map((item, index) => (
                <div key={index} className="bg-orange-50 p-4 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-green-500">
                  <p className="text-xl font-bold text-orange-600">{item.year}</p>
                  <p className="text-gray-700">{item.award}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us Section */}
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">
            {t('about.whyChooseUs.title', 'Why Choose Us')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              {
                icon: <Users className="w-12 h-12 text-green-700" />,
                title: t('about.whyChooseUs.expertTeam.title', 'Expert Team'),
                description: t('about.whyChooseUs.expertTeam.description', 'Our recruiters bring years of industry-specific experience.'),
              },
              {
                icon: <Target className="w-12 h-12 text-green-600" />,
                title: t('about.whyChooseUs.targetedApproach.title', 'Targeted Approach'),
                description: t('about.whyChooseUs.targetedApproach.description', 'We use advanced techniques to find the best candidates.'),
              },
              {
                icon: <Briefcase className="w-12 h-12 text-green-700" />,
                title: t('about.whyChooseUs.diverseOpportunities.title', 'Diverse Opportunities'),
                description: t('about.whyChooseUs.diverseOpportunities.description', 'Access a wide range of job openings across various sectors.'),
              },
              {
                icon: <Award className="w-12 h-12 text-green-600" />,
                title: t('about.whyChooseUs.successRecord.title', 'Success Record'),
                description: t('about.whyChooseUs.successRecord.description', 'We have a proven track record of successful placements.'),
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-all duration-300 border-b-4 border-transparent hover:border-orange-500"
              >
                <div className="flex justify-center mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="bg-green-700 text-white rounded-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              {t('about.cta.title', 'Ready to Take the Next Step?')}
            </h2>
            <p className="text-lg mb-6">
              {t('about.cta.description', 'Whether you\'re looking for your next career move or seeking top talent for your company, we\'re here to help.')}
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/jobs"
                className="bg-white text-green-700 px-6 py-3 rounded-lg font-semibold hover:bg-green-50 transition duration-300 inline-flex items-center"
              >
                {t('about.cta.viewJobs', 'View Job Listings')} <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition duration-300 inline-flex items-center"
              >
                {t('about.cta.contactUs', 'Contact Us')} <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default About;
